import React, { useState, useEffect, useRef, useCallback } from 'react';
import './createPlantDetails.css';
import { getAssetTypes, createPlantAsset, getPlantById, uploadConfigData, getUomsForValidation, SensorUomResponse, CreatePlantAssetPayload, PlantAssetParameter, downloadConfigFiles } from '../../Services/serviceURL';
import { useNavigate } from 'react-router-dom';
import assetIcon from '../../Images/asset-icon.png';
import SearchIcon from '../../Images/search-icon.svg';
import useSearch from '../../hooks/useSearch';
import './createPlant.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import downloadIcon from '../../Images/download-icon.svg';
import Loader from '../../Components/common/loader/loader';

interface AssetParameter {
    tag: string;
    sensorName: string;
    isUserConfigurable: boolean;
    units: {
        code: string;
        description: string;
    }[];
    channelId?: string;
    position?: string;
    hasUom: boolean;
}

interface AssetType {
    typeName: string;
    allowSubAssets: boolean;
    controllerType?: string;
    isUserConfigurable: boolean;
    parameters: AssetParameter[];
}
interface UIParameter {
    tag: string; // Add tag field
    sensor: string;
    position:string;
    businessFriendlyName: string;
    value: string;
    units: string;
    unitOptions: {
        code: string;
        description: string;
    }[];
    originalUnits: {
        code: string;
        description: string;
    }[]; // Add this to store original units for standard probes
    isUserConfigurable?: boolean; // Add this to track if it's a custom probe
    selectedSensorType?: string; // Add this for custom probe sensor type selection
    hasUom: boolean; // Add this to determine if UOM dropdown should be shown
}

interface OperationalParameter {
    sensor: string;
    position:string;
    businessFriendlyName: string;
    value: string;
    unit?: string;
}

interface PlantData {
    customerId: string;
    customerName: string;
    siteId: string;
    siteName: string;
    plantName: string;
    plantStatus: string;
    assets: any[];
}

interface AssetDetails {
    id: string;
    type: string;
    name: string;
    status: string;
    parameters: {
        tag: string;
        sensorName: string;
        position:string;
        displayName: string;
        unit?: string; // Add unit field to store the selected unit
    }[];
    gatewayDeviceType?: string;
    gatewayDeviceId?: string;
    simNumber?: string;
    controllerType?: string;
    controllerDeviceId?: string;
}

const mapToUIParameters = (
    params: AssetParameter[],
    validUoms: SensorUomResponse[] = [],
    preselectDefaults: boolean = false
): UIParameter[] => {
    return params.map(param => {
        let unitOptions = Array.isArray(param.units) ? param.units : [];
        
        // For custom probes (isUserConfigurable: true), provide all available UOMs
        if (param.isUserConfigurable === true && validUoms.length > 0) {
            // Provide all available UOMs for custom probes
            const allUomsMap = new Map();
            validUoms.forEach(sensorUom => {
                sensorUom.uoms.forEach(uom => {
                    allUomsMap.set(uom.code, uom);
                });
            });
            unitOptions = Array.from(allUomsMap.values());
        }
        
        const defaultUnit = preselectDefaults && param.hasUom && param.isUserConfigurable !== true && unitOptions.length > 0
            ? unitOptions[0].code
            : '';
        
        return {
            tag: param.tag,
            sensor: param.sensorName,
            position: param.position || '',
            businessFriendlyName: '',
            value: '',
            units: defaultUnit,
            unitOptions,
            originalUnits: Array.isArray(param.units) ? param.units : [],
            isUserConfigurable: param.isUserConfigurable,
            selectedSensorType: param.isUserConfigurable === true ? '' : undefined,
            hasUom: param.hasUom
        };
    });
};
const CreatePlantDetails: React.FC = () => {
    const [selectedAsset, setSelectedAsset] = useState<string>('');
    const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
    const [selectedType, setSelectedType] = useState<string>('');

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [assetName, setAssetName] = useState<string>('');
    const [assetStatus, setAssetStatus] = useState<string>('Active');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [parameters, setParameters] = useState<UIParameter[]>([]);
    const [plantData, setPlantData] = useState<PlantData | null>(null);
    const [assetHierarchy, setAssetHierarchy] = useState<AssetDetails[]>([]);
    const [selectedAssetFromHierarchy, setSelectedAssetFromHierarchy] = useState<AssetDetails | null>(null);
    const [currentPlantData, setCurrentPlantData] = useState<any>(null); // Add this

    // New state variables for additional fields
    const [gatewayDeviceType, setGatewayDeviceType] = useState<string>('SLE');
    const [gatewayDeviceId, setGatewayDeviceId] = useState<string>('');
    const [simNumber, setSimNumber] = useState<string>('');
    const [simNumberError, setSimNumberError] = useState<string>('');
    const [simNumberTouched, setSimNumberTouched] = useState<boolean>(false);
    const [controllerType, setControllerType] = useState<string>('');
    const [controllerDeviceId, setControllerDeviceId] = useState<string>('');
    const [gatewaySerialError, setGatewaySerialError] = useState<string>('');
    const [gatewaySerialTouched, setGatewaySerialTouched] = useState<boolean>(false);
    const [availableControllerTypes, setAvailableControllerTypes] = useState<string[]>([]);
    const navigate = useNavigate();
    const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
    const [id, setId] = useState<string>('');
    const [validUoms, setValidUoms] = useState<SensorUomResponse[]>([]);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const isRefreshingAfterSave = useRef<boolean>(false);
    const lastFetchedId = useRef<string>('');
    const isExistingAssetSelected = Boolean(selectedAssetFromHierarchy);
    const getGatewaySerialValidationMessage = useCallback((serial: string) => {
        const trimmedSerial = (serial || '').trim();
        if (!trimmedSerial) {
            return '';
        }

        const duplicateAsset = assetHierarchy.find(
            asset =>
                asset.gatewayDeviceId &&
                asset.gatewayDeviceId.trim().toLowerCase() === trimmedSerial.toLowerCase() &&
                asset.id !== selectedAssetId
        );

        return duplicateAsset ? 'Gateway serial number already in use' : '';
    }, [assetHierarchy, selectedAssetId]);

    // State to track original asset data for change detection
    const [originalAssetData, setOriginalAssetData] = useState<{
        selectedAsset: string;
        assetName: string;
        assetStatus: string;
        gatewayDeviceType: string;
        gatewayDeviceId: string;
        simNumber: string;
        controllerType: string;
        controllerDeviceId: string;
        parameters: UIParameter[];
    } | null>(null);

 useEffect(() => {
        // Check localStorage for currentPlantData
        const storedPlantData = localStorage.getItem('currentPlantData');
        if (storedPlantData) {
            try {
                const parsedData = JSON.parse(storedPlantData);
                setCurrentPlantData(parsedData);
            } catch (error) {
                console.error('Error parsing currentPlantData:', error);
            }
        }
    }, []); // Run once on component mount
  // Also reset isInitialLoad when plant changes

    // Track previous plant ID to detect changes
    const [prevPlantId, setPrevPlantId] = useState<string>('');
    useEffect(() => {
        const navState = window.history.state?.usr; // router passes state here
        const passedPlantId = navState?.plantId;

        // Try to get ObjectId for internal tracking (this is used for navigation state)
        const fallbackPlantId = localStorage.getItem('currentPlantId');
        const finalPlantId = passedPlantId || fallbackPlantId;

        if (!finalPlantId) {
            // Check if we at least have a GUID
            const plantGuid = localStorage.getItem('currentplantguid');
            if (!plantGuid) {
                setError('No plant ID found');
                navigate('/plants');
                return;
            }
        }

        // Check if plant ID has changed
        if (prevPlantId && prevPlantId !== finalPlantId) {
            // Reset all form and hierarchy state when switching plants
            resetAllFields();
            setAssetHierarchy([]);
            setPlantData(null);
            setSelectedAssetFromHierarchy(null);
            setOriginalAssetData(null);
            setError(null);
        }

        if (finalPlantId) {
            setId(finalPlantId); // update your internal ID state here
            localStorage.setItem('currentPlantId', finalPlantId); // keep both in sync
            // Don't set prevPlantId here - let the fetch useEffect handle it
        }
    }, [navigate, prevPlantId]);

      useEffect(() => {
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        return () => {
          document.body.style.overflow = previousBodyOverflow;
          document.documentElement.style.overflow = previousHtmlOverflow;
        };
      }, []);

    useEffect(() => {
        const fetchAssetTypes = async () => {
            setLoading(true);
            try {
                // Fetch both asset types and valid UOMs in parallel
                const [assetTypesResponse, uomsResponse] = await Promise.all([
                    getAssetTypes(),
                    getUomsForValidation()
                ]);
                
                // Set valid UOMs for custom probe validation
                setValidUoms(uomsResponse || []);

                if (assetTypesResponse.Success && Array.isArray(assetTypesResponse.Data)) {

                    setAssetTypes(assetTypesResponse.Data);

                    if (assetTypesResponse.Data.length > 0) {
                        const firstAsset = assetTypesResponse.Data[0];
                        setSelectedAsset(firstAsset.typeName);
                        // Set initial controller type - handle cases where it might be undefined
                        const initialControllerType = firstAsset.controllerType || '';
                        setControllerType(initialControllerType);
                        // Only show the controllerType for the first asset type if it exists
                        setAvailableControllerTypes(initialControllerType ? [initialControllerType] : []);

                        // Transform API parameters into UIParameter structure with proper unit options
                        const initialParameters = mapToUIParameters(firstAsset.parameters, uomsResponse || [], true);
                        setParameters(initialParameters);
                    }
                } else {
                    setError('No asset types found');
                }
            } catch (err) {

                setError('Failed to load asset types');
            } finally {
                setLoading(false);
            }
        };

        fetchAssetTypes();
    }, []);

    // Update unit options for existing parameters when asset types are loaded
    useEffect(() => {
        if (assetTypes.length > 0 && parameters.length > 0 && validUoms.length > 0) {
            const updatedParameters = parameters.map(param => {
                // Find the asset type for the current selected asset
                const currentAssetType = assetTypes.find(type => type.typeName === selectedAsset);
                if (currentAssetType) {
                    // Find the matching parameter in the asset type to get unit options
                    const typeParam = currentAssetType.parameters.find(tp => tp.sensorName === param.sensor);
                    if (typeParam) {
                        let unitOptions = Array.isArray(typeParam.units) ? typeParam.units : [];
                        
                        // For custom probes (isUserConfigurable: true), use UOMs from validation API
                        if (typeParam.isUserConfigurable === true) {
                            const sensorUoms = validUoms.find(uom => uom.sensorName === param.sensor);
                            if (sensorUoms) {
                                unitOptions = sensorUoms.uoms;

                            }
                        }
                        
                        return {
                            ...param,
                            unitOptions: unitOptions
                        };
                    }
                }
                return param;
            });
            
            // Only update if there are actual changes to unit options
            const hasChanges = updatedParameters.some((param, index) => 
                JSON.stringify(param.unitOptions) !== JSON.stringify(parameters[index].unitOptions)
            );
            
            if (hasChanges) {
                setParameters(updatedParameters);
            }
        }
    }, [assetTypes, selectedAsset, validUoms]);


    // Handle asset type changes only when not loading from hierarchy
    useEffect(() => {
        // Skip if asset is being loaded from hierarchy or if no asset types are available
        if (selectedAssetFromHierarchy || assetTypes.length === 0) {
            return;
        }

        const selectedAssetType = assetTypes.find(type => type.typeName === selectedAsset);
        if (selectedAssetType) {

            
            // Only update parameters, don't clear other fields unless it's a new selection
            const newParameters = mapToUIParameters(selectedAssetType.parameters, validUoms, !selectedAssetFromHierarchy);

            setParameters(newParameters);
            
            // Update controller type based on asset type
            updateControllerType(selectedAsset);
        }
    }, [selectedAsset, assetTypes, selectedAssetFromHierarchy]);

    // Initialize default values when component first loads
    useEffect(() => {
        if (assetTypes.length > 0 && !selectedAsset && !selectedAssetFromHierarchy) {
            const firstAssetType = assetTypes[0];
            setSelectedAsset(firstAssetType.typeName);
            updateControllerType(firstAssetType.typeName);
        }
    }, [assetTypes, selectedAsset, selectedAssetFromHierarchy]);

    useEffect(() => {
        if (gatewaySerialTouched) {
            setGatewaySerialError(getGatewaySerialValidationMessage(gatewayDeviceId));
        }
    }, [assetHierarchy, selectedAssetId, gatewayDeviceId, gatewaySerialTouched, getGatewaySerialValidationMessage]);

    const handleAssetSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };
    const handleGatewaySerialChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setGatewayDeviceId(value);

        if (gatewaySerialTouched) {
            setGatewaySerialError(getGatewaySerialValidationMessage(value));
        } else if (gatewaySerialError) {
            setGatewaySerialError('');
        }
    };

    const handleGatewaySerialBlur = () => {
        setGatewaySerialTouched(true);
        setGatewaySerialError(getGatewaySerialValidationMessage(gatewayDeviceId));
    };

    const resetGatewaySerialValidation = () => {
        setGatewaySerialTouched(false);
        setGatewaySerialError('');
    };
    //validation for sim number
    const validateSimNumber = (sim: string): string => {
    const trimmedSim = (sim || '').trim();
    
    if (!trimmedSim) {
        return '' ;
    }
     if (/\s/.test(trimmedSim)) {
        return 'SIM number should not contain spaces';
    }
    //Check if contains only digits
    if (!/^\d+$/.test(String(trimmedSim))) {
    return 'SIM number should contain only digits (0-9)';
}
    
    // Check length constraints
    if (trimmedSim.length < 5) {
        return 'SIM number should be at least 5 digits';
    }
    
    return ''; // No error
};  
   // sim number handler
  const handleSimNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
     const cleanValue = value.replace(/\s/g, '').replace(/\D/g, '');
    setSimNumber(value);

    // Real-time validation when touched
    if (simNumberTouched) {
        setSimNumberError(validateSimNumber(value));
    }
    };

   const handleSimNumberBlur = () => {
    setSimNumberTouched(true);
    setSimNumberError(validateSimNumber(simNumber));
   };

   const resetSimNumberValidation = () => {
    setSimNumberTouched(false);
    setSimNumberError('');
   };
    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selected = e.target.value;
        
        // Clear asset hierarchy selection when manually changing type
        if (selectedAssetFromHierarchy) {
            setSelectedAssetFromHierarchy(null);
            setSelectedAssetId(null);
            setOriginalAssetData(null);
        }
        
        setSelectedAsset(selected);
        
        // Clear form fields for new asset type selection
        setAssetName('');
        setAssetStatus('Active');
        setGatewayDeviceType('SLE');
        setGatewayDeviceId('');
        resetGatewaySerialValidation();
        setSimNumber('');
        setControllerDeviceId('');
        
        // Update controller type and parameters
        updateControllerType(selected);
        
        const matchedAsset = assetTypes.find(item => item.typeName === selected);
        if (matchedAsset) {
            const updatedParameters = mapToUIParameters(matchedAsset.parameters, validUoms, true);
            setParameters(updatedParameters);
        }
    };
    const resetAllFields = () => {
        // Clear all form state
        setSelectedAssetFromHierarchy(null);
        setSelectedAssetId(null);
        setOriginalAssetData(null);
        setAssetName('');
        setAssetStatus('Active');
        setSearchQuery('');
        setGatewayDeviceType('SLE');
        setGatewayDeviceId('');
        setSimNumber('');
        setControllerDeviceId('');
        setError(null);
         //reset validation states
         resetGatewaySerialValidation();
         resetSimNumberValidation();
        // Reset to first asset type if available
        if (assetTypes.length > 0) {
            const firstAssetType = assetTypes[0];
            setSelectedAsset(firstAssetType.typeName);
            const controllerTypeValue = firstAssetType.controllerType || '';
            setControllerType(controllerTypeValue);
            setAvailableControllerTypes(controllerTypeValue ? [controllerTypeValue] : []);
            
            const defaultParams = mapToUIParameters(firstAssetType.parameters, validUoms, true);
            setParameters(defaultParams);
        } else {
            setSelectedAsset('');
            setControllerType('');
            setAvailableControllerTypes([]);
            setParameters([]);
        }
    };

    // Function to handle sensor type selection for custom probes
    const handleSensorTypeChange = (paramIndex: number, selectedSensorType: string) => {
        const updatedParameters = [...parameters];
        const param = updatedParameters[paramIndex];
        
        // Update the selected sensor type
        param.selectedSensorType = selectedSensorType;
        param.units = ''; // Reset selected units when sensor type changes
        
        // Find UOMs for the selected sensor type
        if (selectedSensorType && validUoms.length > 0) {
            const sensorUoms = validUoms.find(uom => uom.sensorName === selectedSensorType);
            if (sensorUoms) {
                param.unitOptions = sensorUoms.uoms;

            } else {
                param.unitOptions = [];

            }
        } else {
            param.unitOptions = [];
        }
        
        setParameters(updatedParameters);
    };

    // Function to validate UOMs for custom probes
    const validateCustomProbeUoms = async (assetType: AssetType, parameters: UIParameter[]): Promise<{ isValid: boolean; invalidSensors: string[] }> => {
        try {
            // Use the already loaded valid UOMs instead of fetching again
            const invalidSensors: string[] = [];
            
            // Check each parameter for custom probes
            for (const param of parameters) {
                // Find the corresponding asset type parameter to check if it's user configurable
                const assetTypeParam = assetType.parameters.find(atp => atp.sensorName === param.sensor);
                
                if (assetTypeParam && assetTypeParam.isUserConfigurable === true) {
                    // This is a custom probe, validate the UOM using selected sensor type and business friendly name

                    
                    // Only validate if user has entered a business friendly name
                    if (param.businessFriendlyName.trim()) {
                        // Check if a unit is selected (required for custom probes with business friendly names)
                        if (param.units) {
                            // Check if the selected unit code exists in any of the valid UOMs
                            // Since we now provide all UOMs to custom probes, validate against all available units
                            const allValidUnits: string[] = [];
                            validUoms.forEach(sensorUom => {
                                sensorUom.uoms.forEach(uom => {
                                    allValidUnits.push(uom.code);
                                });
                            });
                            
                            const isValidUnit = allValidUnits.includes(param.units);
                            
                            if (!isValidUnit) {
                                invalidSensors.push(`${param.sensor} (invalid unit: ${param.units})`);
                            }
                            // Valid case - custom probe has business friendly name and valid unit
                        } else {
                            // User entered a business friendly name but didn't select a unit
                            invalidSensors.push(`${param.sensor} (no unit selected)`);
                        }
                    } else {
                        // Allow empty custom probe sensors - skip validation

                    }
                } else {
                    // This is a standard probe (isUserConfigurable: false), no validation needed

                }
            }
            

            
            return {
                isValid: invalidSensors.length === 0,
                invalidSensors
            };
        } catch (error) {

            // If validation fails, allow saving but log the error
            toast.warn('UOM validation encountered an error. Proceeding with save.');
            return { isValid: true, invalidSensors: [] };
        }
    };

const handleSave = async () => {
    // Prevent multiple simultaneous save operations
    if (loading) {
        return;
    }

    try {
        setLoading(true);
        setIsSaving(true);
        isRefreshingAfterSave.current = true;
        setError(null);

        // Check if there are any changes for existing assets
        if (selectedAssetFromHierarchy && !hasAssetChanges()) {
            toast.info('No changes detected. Save operation cancelled.');
            setLoading(false);
            return;
        }

        // === CONSOLIDATED VALIDATION ===
        const missingFields: string[] = [];

        // Check each mandatory field
        if (!assetName.trim()) missingFields.push('Asset Name');
        if (!gatewayDeviceId.trim()) missingFields.push('Gateway Serial Number');
        if (!simNumber.trim()) missingFields.push('SIM Number');
        if (!controllerDeviceId.trim()) missingFields.push('Controller Serial Number');
        
        // Validate SIM number format
        const simNumberValidationError = simNumber.trim() ? validateSimNumber(simNumber) : '';

        // Check for duplicate asset name
        const isDuplicate = assetHierarchy.some(
            (asset) =>
                asset.name.trim().toLowerCase() === assetName.trim().toLowerCase() &&
                asset.id !== selectedAssetId
        );

        // Check for duplicate gateway serial
        const gatewaySerialValidationMessage = getGatewaySerialValidationMessage(gatewayDeviceId);

        // === SHOW CONSOLIDATED VALIDATION MESSAGES ===
        if (missingFields.length > 0) {
            let errorMessage = '';
            
            if (missingFields.length === 1) {
                errorMessage = `${missingFields[0]} is required`;
            } else if (missingFields.length === 2) {
                errorMessage = `${missingFields[0]} and ${missingFields[1]} are not provided`;
            } else if (missingFields.length === 3) {
                errorMessage = 'Asset Name, Gateway Serial Number, and SIM Number are not provided';
            }
              else if (missingFields.length === 4){
                errorMessage = 'Asset Name, Gateway Serial Number, SIM Number, Controller Serial Number are not provided';
              }
            
            toast.error(errorMessage);
            setLoading(false);
            return;
        }
        
        // Show SIM number format errors
        if (simNumberValidationError) {
            toast.error(simNumberValidationError);
            setSimNumberTouched(true);
            setSimNumberError(simNumberValidationError);
            setLoading(false);
            return;
        }

        if (!selectedAsset.trim()) {
            toast.error('Asset type must be selected');
            setLoading(false);
            return;
        }

        if (isDuplicate) {
            toast.error('Duplicate asset name is not allowed');
            setLoading(false);
            return;
        }

        if (gatewaySerialValidationMessage) {
            setGatewaySerialTouched(true);
            setGatewaySerialError(gatewaySerialValidationMessage);
            toast.error(gatewaySerialValidationMessage);
            setLoading(false);
            return;
        }

        // Get plantId - try GUID version first, then fallback to regular plantId
        let plantId = localStorage.getItem('currentplantguid') || localStorage.getItem('currentPlantId');
        if (!plantId) {
            toast.error('Plant ID not found. Please create/select a plant first.');
            setLoading(false);
            return;
        }

        const selectedAssetType = assetTypes.find(type => type.typeName === selectedAsset);
        if (!selectedAssetType) {
            toast.error('Selected asset type not found.');
            setLoading(false);
            return;
        }

        // === PAYLOAD PREPARATION ===
        const mappedParameters = Array.isArray(parameters)
            ? parameters
                .filter(param => {
                    // Always include all parameters (both standard probes and custom probes)
                    // Custom probes should be included even if they're empty
                    return true;
                })
                .map(param => {
                    // For custom probes, use the selectedSensorType as the sensorName
                    const sensorName = param.isUserConfigurable === true && param.selectedSensorType 
                        ? param.selectedSensorType 
                        : param.sensor;
                    
                    // Find the unit description from the available options
                    let unitDescription = '';
                    if (param.units) {
                        if (param.isUserConfigurable === true && param.unitOptions.length > 0) {
                            // For custom probes, find description from unitOptions
                            const unitOption = param.unitOptions.find(opt => opt.code === param.units);
                            unitDescription = unitOption?.description || '';
                        } else if (param.originalUnits.length > 0) {
                            // For standard probes, find description from originalUnits
                            const unitOption = param.originalUnits.find(opt => opt.code === param.units);
                            unitDescription = unitOption?.description || '';
                        }
                    }
                    
                    return {
                        tag: param.tag, // Use the actual tag from asset type
                        sensorName: sensorName,
                        position: param.position,
                        displayName: param.businessFriendlyName,
                        unit: param.units,
                        unitDescription: unitDescription
                    };
                })
            : [];

        const payload = {
            PlantId: plantId,
            ...(selectedAssetId && { AssetId: selectedAssetId }),
            Type: selectedAsset,
            Name: assetName,
            Status: assetStatus,
            GatewayDeviceType: gatewayDeviceType,
            GatewayDeviceId: gatewayDeviceId,
            SimNumber: simNumber,
            ControllerType: controllerType,
            ControllerDeviceId: controllerDeviceId,
            Parameters: mappedParameters
        };

        // === API CALL ===
        const response = await createPlantAsset(payload);

        if (response.Success) {
            toast.success('Asset saved successfully!');

            // Create the asset data
            const assetData: AssetDetails = {
                id: selectedAssetId || response.Data || crypto.randomUUID(),
                type: selectedAsset,
                name: assetName,
                status: assetStatus,
                parameters: mappedParameters,
                gatewayDeviceType: gatewayDeviceType,
                gatewayDeviceId: gatewayDeviceId,
                simNumber: simNumber,
                controllerType: controllerType,
                controllerDeviceId: controllerDeviceId
            };
            
            // Update asset hierarchy - either add new or update existing
            if (selectedAssetId) {
                // Update existing asset
                setAssetHierarchy(prevHierarchy => 
                    prevHierarchy.map(asset => 
                        asset.id === selectedAssetId ? assetData : asset
                    )
                );
                
                // ✅ Update plantData state for existing asset
                setPlantData(prevPlantData => {
                    if (!prevPlantData) return prevPlantData;
                    return {
                        ...prevPlantData,
                        assets: (prevPlantData.assets || []).map(asset => 
                            asset.id === selectedAssetId ? assetData : asset
                        )
                    };
                });
                
                // ✅ Reset original data after successful save to disable the button
                const updatedOriginalData = {
                    selectedAsset: selectedAsset,
                    assetName: assetName,
                    assetStatus: assetStatus,
                    gatewayDeviceType: gatewayDeviceType,
                    gatewayDeviceId: gatewayDeviceId,
                    simNumber: simNumber,
                    controllerType: controllerType,
                    controllerDeviceId: controllerDeviceId,
                    parameters: parameters.map(param => ({ ...param })) // Deep copy
                };
                setOriginalAssetData(updatedOriginalData);
            } else {
                // Add new asset to hierarchy
                setAssetHierarchy(prevHierarchy => [...prevHierarchy, assetData]);
                
                // ✅ CRITICAL FIX: Update plantData state with new asset
                setPlantData(prevPlantData => {
                    if (!prevPlantData) {
                        // If no plantData exists, create a basic structure
                        return {
                            customerId: '',
                            customerName: '',
                            siteId: '',
                            siteName: '',
                            plantName: '',
                            plantStatus: 'Active',
                            assets: [assetData]
                        };
                    }
                    
                    return {
                        ...prevPlantData,
                        assets: [...(prevPlantData.assets || []), assetData]
                    };
                });
            }
            
            // Update localStorage with the asset data
            const storedPlantData = localStorage.getItem('currentPlantData');
            if (storedPlantData) {
                try {
                    const parsedPlantData = JSON.parse(storedPlantData);
                    if (!parsedPlantData.assets) {
                        parsedPlantData.assets = [];
                    }
                    
                    if (selectedAssetId) {
                        // Update existing asset in localStorage
                        const assetIndex = parsedPlantData.assets.findIndex((asset: any) => asset.id === selectedAssetId);
                        if (assetIndex !== -1) {
                            parsedPlantData.assets[assetIndex] = assetData;
                        }
                    } else {
                        // Add new asset to localStorage
                        parsedPlantData.assets.push(assetData);
                    }
                    
                    localStorage.setItem('currentPlantData', JSON.stringify(parsedPlantData));
                } catch (parseError) {
                    console.error('Error updating localStorage:', parseError);
                }
            } else {
                // If no plantData in localStorage, create it
                try {
                    const newPlantData = {
                        customerId: plantData?.customerId || '',
                        customerName: plantData?.customerName || '',
                        siteId: plantData?.siteId || '',
                        siteName: plantData?.siteName || '',
                        plantName: plantData?.plantName || '',
                        plantStatus: plantData?.plantStatus || 'Active',
                        assets: [assetData]
                    };
                    localStorage.setItem('currentPlantData', JSON.stringify(newPlantData));
                } catch (error) {
                    console.error('Error creating localStorage plant data:', error);
                }
            }

            // Only reset fields when creating a new asset, not when editing
            if (!selectedAssetId) {
                resetAllFields();
                const firstAssetType = assetTypes.length > 0 ? assetTypes[0] : null;
                const isBasicSystem = firstAssetType && selectedAsset === firstAssetType.typeName;

                if (isBasicSystem) {
                    setAssetName('');
                } else if (firstAssetType) {
                    setSelectedAsset(firstAssetType.typeName);
                    setAssetStatus('Active');
                    setAssetName('');
                    setParameters(mapToUIParameters(firstAssetType.parameters, validUoms, true));
                }
            }

            // Scroll to top of the page after successful save
            window.scrollTo({ top: 0, behavior: 'smooth' });

        } else {
            toast.error(response.Message || 'Failed to save asset. Please try again.');
        }

    } catch (err: any) {
        // Extract message from API response if available
        const apiMessage = err?.response?.data?.Message || err?.message || 'Failed to save asset. Please retry.';
        toast.error(apiMessage);
    } finally {
        setLoading(false);
        setIsSaving(false);
        // Use setTimeout to ensure all state updates are complete before clearing the flag
        setTimeout(() => {
            isRefreshingAfterSave.current = false;
        }, 100);
    }
    
};
    const handleCancel = () => {
        // Reset all form fields to their initial values
        setSelectedAsset(assetTypes.length > 0 ? assetTypes[0].typeName : '');
        setAssetName('');
        setAssetStatus('Active');
        setSearchQuery('');
        setGatewayDeviceType('SLE');
        setGatewayDeviceId('');
        setSimNumber('');
        setControllerType('');
        setControllerDeviceId('');
        resetGatewaySerialValidation();

        // Reset parameters based on the first asset type
        if (assetTypes.length > 0) {
            const initialParameters = mapToUIParameters(assetTypes[0].parameters, validUoms, true);

            setParameters(initialParameters);
        } else {
            setParameters([]);
        }

        navigate('/plants');
    };

    // Tab navigation handler
    const handleTabNavigation = (tabNumber: number) => {
        const plantGuid = localStorage.getItem('currentplantguid');
        
        switch (tabNumber) {
            case 1:
                // Navigate back to Create Plant page
                if (plantGuid) {
                    navigate(`/create-plant/${plantGuid}`);
                } else {
                    navigate('/create-plant');
                }
                break;
            case 2:
                // Current page - do nothing
                break;
            case 3:
                // Navigate to Notifications
                navigate('/create-plant-details/notification');
                break;
            default:
                break;
        }
    };
    
    const handleAddAsset = () => {
        // Use the resetAllFields function to ensure consistent reset behavior
        resetAllFields();
    };

    // Function to handle downloading config files
    const handleDownloadConfigFiles = async () => {
        if (!selectedAssetFromHierarchy) {
            toast.warn('Please select an asset to download config files.');
            return;
        }

        try {
            setLoading(true);
            
            // Use the API to download config files
            const result = await downloadConfigFiles(selectedAssetFromHierarchy.id);
            
            if (result.success) {
                toast.success(result.message);
            } else {
                toast.error(result.message);
            }
        } catch (error) {
            console.error('Error downloading config files:', error);
            toast.error('Failed to download config files. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAsset = async () => {
        // Get plantId - try GUID version first, then fallback to regular plantId
        let plantId = localStorage.getItem('currentplantguid') || localStorage.getItem('currentPlantId');
        if (!plantId) {
            toast.error('Plant ID not found. Please create/select a plant first.');
            setLoading(false);
            return;
        }


        

        
        // Create the asset object
        const assetObject = {
            type: selectedAsset,
            name: assetName,
            status: assetStatus,
            controllerType,
            controllerDeviceId,
            gatewayDeviceType,
            gatewayDeviceId,
            simNumber,
            parameters: Array.isArray(parameters)
                ? parameters
                    .filter(param => {
                        // Always include all parameters (both standard probes and custom probes)
                        // Custom probes should be included even if they're empty
                        return true;
                    })
                    .map(param => {
                        // Find the unit description from the available options
                        let unitDescription = '';
                        if (param.units) {
                            if (param.isUserConfigurable === true && param.unitOptions.length > 0) {
                                // For custom probes, find description from unitOptions
                                const unitOption = param.unitOptions.find(opt => opt.code === param.units);
                                unitDescription = unitOption?.description || '';
                            } else if (param.originalUnits.length > 0) {
                                // For standard probes, find description from originalUnits
                                const unitOption = param.originalUnits.find(opt => opt.code === param.units);
                                unitDescription = unitOption?.description || '';
                            }
                        }
                        
                        return {
                            tag: param.tag, // Use the actual tag from asset type
                            sensorName: param.sensor,
                            position:param.position,
                            displayName: param.businessFriendlyName,
                            unit: param.units || '',
                            unitDescription: unitDescription
                        };
                    })
                : []
        };

        // Create the payload with flat structure
        const newAssetPayload = {
            PlantId: plantId,
            ...(selectedAssetId && { AssetId: selectedAssetId }),
            Type: assetObject.type,
            Name: assetObject.name,
            Status: assetObject.status,
            ControllerType: assetObject.controllerType,
            ControllerDeviceId: assetObject.controllerDeviceId,
            GatewayDeviceType: assetObject.gatewayDeviceType,
            GatewayDeviceId: assetObject.gatewayDeviceId,
            SimNumber: assetObject.simNumber,
            Parameters: assetObject.parameters
        };



        try {
            const response = await createPlantAsset(newAssetPayload);
            if (response.Success) {
                const newAssetForHierarchy: AssetDetails = {
                    id: response.Id, // Use response ID or generate one
                    type: newAssetPayload.Type,
                    name: newAssetPayload.Name,
                    status: newAssetPayload.Status,
                    parameters: newAssetPayload.Parameters,
                    gatewayDeviceType: newAssetPayload.GatewayDeviceType,
                    gatewayDeviceId: newAssetPayload.GatewayDeviceId,
                    simNumber: newAssetPayload.SimNumber,
                    controllerType: newAssetPayload.ControllerType,
                    controllerDeviceId: newAssetPayload.ControllerDeviceId
                };
                setAssetHierarchy(prevHierarchy => [...prevHierarchy, newAssetForHierarchy]);

            } else {
                toast.error(`Failed to add asset: ${response.Message}`);
            }
        } catch (error) {
            toast.error('Error adding asset. Please try again.');
        }
    };
    const handleBack = () => {
        // Use proper plant GUID for navigation (URL parameter)
        const plantGuid = localStorage.getItem('currentplantguid');
        if (plantGuid) {
            navigate(`/create-plant/${plantGuid}`); // Go back to edit mode for the same plant
        } else {
            navigate('/plants'); // Fallback
        }
    };

    const handleAssetClick = (asset: AssetDetails) => {
        setSelectedAssetFromHierarchy(asset);

        setSelectedAsset(asset.type);
        setAssetName(asset.name);
        setAssetStatus(asset.status);
        setSelectedAssetId(asset.id);

        // Set gateway and controller device info
        setGatewayDeviceType(asset.gatewayDeviceType ?? '');
        setGatewayDeviceId(asset.gatewayDeviceId ?? '');
        resetGatewaySerialValidation();
        setSimNumber(asset.simNumber ?? '');
        setControllerDeviceId(asset.controllerDeviceId ?? '');
        
        // Set controller type - use saved value or fallback to asset type definition
        const matchingAssetType = assetTypes.find(type => type.typeName === asset.type);
        const controllerTypeValue = asset.controllerType || matchingAssetType?.controllerType || '';
        setControllerType(controllerTypeValue);
        setAvailableControllerTypes(controllerTypeValue ? [controllerTypeValue] : []);

        // Find the asset type to get unit options
        const assetType = matchingAssetType;

        //  Map parameters with unit options from asset type
        const assetParameters: UIParameter[] = [];
        
        // First, ensure all asset type parameters are represented
        if (assetType?.parameters) {
            assetType.parameters.forEach(typeParam => {
                // Find matching saved parameter
                const savedParam = Array.isArray(asset.parameters) 
                    ? asset.parameters.find((param: any) => {
                        if (typeParam.isUserConfigurable === true) {
                            // For custom probes, match by tag
                            return typeParam.tag === param.tag;
                        } else {
                            // For standard probes, match by sensor name
                            return typeParam.sensorName === param.sensorName;
                        }
                    })
                    : null;
                
                const hasUom = typeParam.hasUom;
                
                let unitOptions = Array.isArray(typeParam.units) ? typeParam.units : [];
                let selectedSensorType = undefined;
                let position = savedParam?.position || typeParam.position || '';
                let businessFriendlyName = '';
                let units = '';
                
                // For custom probes (isUserConfigurable: true), always provide unit options
                if (typeParam.isUserConfigurable === true && validUoms.length > 0) {
                    // Always provide all available UOMs for custom probes
                    const allUomsMap = new Map();
                    validUoms.forEach(sensorUom => {
                        sensorUom.uoms.forEach(uom => {
                            allUomsMap.set(uom.code, uom);
                        });
                    });
                    unitOptions = Array.from(allUomsMap.values());
                    
                    if (savedParam && savedParam.displayName && savedParam.displayName.trim()) {
                        businessFriendlyName = savedParam.displayName;
                        units = hasUom ? (savedParam.unit || '') : '';
                        
                        // Try to find the sensor type from the saved sensorName
                        const sensorUoms = validUoms.find(uom => uom.sensorName === savedParam.sensorName);
                        if (sensorUoms) {
                            selectedSensorType = savedParam.sensorName; // The saved sensorName is actually the sensor type for custom probes
                        } else {
                            selectedSensorType = 'Custom'; // Default for custom entries
                        }
                    } else {
                        // Custom probe with no saved data - still provide unit options
                        selectedSensorType = '';
                    }
                } else {
                    // For standard probes, use saved values if available
                    if (savedParam) {
                        businessFriendlyName = savedParam.displayName || '';
                        units = hasUom ? (savedParam.unit || '') : '';
                    }
                }
                
                assetParameters.push({
                    tag: typeParam.tag,
                    sensor: typeParam.sensorName,
                    position,
                    businessFriendlyName: businessFriendlyName,
                    value: '',
                    units: units,
                    unitOptions: unitOptions,
                    originalUnits: Array.isArray(typeParam.units) ? typeParam.units : [],
                    isUserConfigurable: typeParam.isUserConfigurable,
                    selectedSensorType: selectedSensorType,
                    hasUom: typeParam.hasUom
                });
            });
        }

        setParameters(assetParameters);

        // Store original asset data for change detection with deep copy of parameters
        const originalData = {
            selectedAsset: asset.type,
            assetName: asset.name,
            assetStatus: asset.status,
            gatewayDeviceType: asset.gatewayDeviceType ?? '',
            gatewayDeviceId: asset.gatewayDeviceId ?? '',
            simNumber: asset.simNumber ?? '',
            controllerType: asset.controllerType ?? '',
            controllerDeviceId: asset.controllerDeviceId ?? '',
            // Create deep copy of parameters to prevent reference issues
            parameters: assetParameters.map(param => ({
                ...param,
                businessFriendlyName: param.businessFriendlyName || '',
                units: param.units || '',
                selectedSensorType: param.selectedSensorType || '',
                value: param.value || '',
                unitOptions: param.unitOptions ? [...param.unitOptions] : []
            }))
        };
        
        setOriginalAssetData(originalData);
    };

    useEffect(() => {
        const fetchPlantDetails = async () => {
            // Try to get GUID first, then fallback to ObjectId
            const plantGuid = localStorage.getItem('currentplantguid');
            const plantObjectId = localStorage.getItem('currentPlantId');
            
            if (!plantGuid && !plantObjectId) {
                setError('No plant ID found');
                navigate('/plants');
                return;
            }

            try {
                setLoading(true);
                
                // Clear existing asset hierarchy when fetching new plant data
                setAssetHierarchy([]);
                setSelectedAssetFromHierarchy(null);
                setOriginalAssetData(null);
                
                // Try to fetch from API first
                let data = null;
                let guidToUse = null;
                
                try {
                    // Priority order for getting GUID:
                    // 1. currentplantguid from localStorage
                    // 2. plantId from stored plant data
                    // 3. Skip API call if no valid GUID
                    
                    if (plantGuid) {
                        guidToUse = plantGuid;
                    } else {
                        // Check if stored plant data has a GUID
                        const storedPlantData = localStorage.getItem('currentPlantData');
                        if (storedPlantData) {
                            try {
                                const parsedData = JSON.parse(storedPlantData);
                                if (parsedData.plantId) {
                                    guidToUse = parsedData.plantId;
                                }
                            } catch (parseError) {
                                // Error parsing stored plant data
                            }
                        }
                    }
                    
                    // Only make API call if we have a valid GUID
                    if (guidToUse) {
                        // Validate GUID format before making API call
                        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
                        if (guidRegex.test(guidToUse)) {
                            const response = await getPlantById(guidToUse);
                            if (response.data.Success && response.data.Data) {
                                data = response.data.Data;
                            }
                        }
                    }
                } catch (apiError) {
                    // API fetch failed, trying localStorage
                }

                // If API failed or returned no data, try localStorage (for create mode)
                if (!data) {
                    const storedPlantData = localStorage.getItem('currentPlantData');
                    if (storedPlantData) {
                        try {
                            data = JSON.parse(storedPlantData);
                            // Ensure assets array exists
                            if (!data.assets) {
                                data.assets = [];
                            }
                        } catch (parseError) {
                            // Error parsing stored plant data
                        }
                    }
                }

                if (data) {
                    setPlantData(data);
                    
                    if (data.customerId) {
                        localStorage.setItem('customerid', data.customerId);
                    }

                    // Extract full asset details for hierarchy
                    const assetHierarchy: AssetDetails[] = (data.assets || []).map((asset: any) => {
                        return {
                            id: asset.id || crypto.randomUUID(),
                            type: asset.type,
                            name: asset.name,
                            status: asset.status,
                            parameters: asset.parameters || [],
                            gatewayDeviceType: asset.gatewayDeviceType || '',
                            gatewayDeviceId: asset.gatewayDeviceId || '',
                            simNumber: asset.simNumber || '',
                            controllerType: asset.controllerType || '',
                            controllerDeviceId: asset.controllerDeviceId || ''
                        };
                    });
                    
                    setAssetHierarchy(assetHierarchy);
                } else {
                    // If neither API nor localStorage has data, set empty state
                    setPlantData({ 
                        customerId: '', 
                        customerName: '', 
                        siteId: '', 
                        siteName: '', 
                        plantName: '', 
                        plantStatus: 'Active', 
                        assets: [] 
                    });
                    setAssetHierarchy([]);
                }
            } catch (err) {
                setError('Failed to load plant details');
            } finally {
                setLoading(false);
            }
        };

        // Only fetch if we have an ID, it's different from previous, and we haven't already fetched it
        if (id && id !== prevPlantId && !isSaving && !isRefreshingAfterSave.current && id !== lastFetchedId.current) {
            // Additional check: only fetch if we don't already have matching plant data
            const currentPlantGuid = localStorage.getItem('currentplantguid');
            const currentPlantId = localStorage.getItem('currentPlantId');
            
            // Only fetch if the ID we're trying to fetch matches what's in localStorage
            // This prevents fetching when IDs are inconsistent
            if (id === currentPlantGuid || id === currentPlantId) {
                fetchPlantDetails();
                lastFetchedId.current = id;
            }
            setPrevPlantId(id);
        }
    }, [navigate, id, prevPlantId, isSaving]);

    // Asset Hierarchy Search
    const {
        searchText: assetSearchText,
        filteredData: filteredAssets,
        handleSearchChange: handleAssetSearchChange,
        setSearchText: setAssetSearchText
    } = useSearch(assetHierarchy);
    
    // Cleanup effect when navigating away from plant details
    useEffect(() => {
        return () => {
            const currentPath = window.location.pathname;
            // If navigating completely away from plant-related pages, clear state
            if (!currentPath.includes('create-plant') && !currentPath.includes('plants')) {
                localStorage.removeItem('currentPlantData');
                localStorage.removeItem('currentPlantId');
                localStorage.removeItem('currentplantguid');
                localStorage.removeItem('customerid');
            }
        };
    }, []);
    const controllerTypeOptions = Array.isArray(assetTypes)
        ? Array.from(new Set(assetTypes.map(asset => asset.controllerType)))
        : [];


    const updateControllerType = (selectedTypeName: string) => {
        const selectedAssetType = assetTypes.find(type => type.typeName === selectedTypeName);
        if (selectedAssetType) {
            const controllerTypeValue = selectedAssetType.controllerType || '';
            setControllerType(controllerTypeValue);
            // Only show the controllerType for the selected asset type if it exists
            setAvailableControllerTypes(controllerTypeValue ? [controllerTypeValue] : []);
        } else {
            setControllerType('');
            setAvailableControllerTypes([]);
        }
    };

    // Function to check if any changes have been made to the asset
    const hasAssetChanges = (): boolean => {
    if (!originalAssetData || !selectedAssetFromHierarchy) {
        return true;
    }

    // Compare basic fields
    if (
        originalAssetData.selectedAsset !== selectedAsset ||
        originalAssetData.assetName !== assetName ||
        originalAssetData.assetStatus !== assetStatus ||
        originalAssetData.gatewayDeviceType !== gatewayDeviceType ||
        originalAssetData.gatewayDeviceId !== gatewayDeviceId ||
        originalAssetData.simNumber !== simNumber ||
        originalAssetData.controllerType !== controllerType ||
        originalAssetData.controllerDeviceId !== controllerDeviceId
    ) {
        return true;
    }

    // Compare parameters with null safety
    if (originalAssetData.parameters.length !== parameters.length) {
        return true;
    }

    for (let i = 0; i < parameters.length; i++) {
        const currentParam = parameters[i];
        const originalParam = originalAssetData.parameters[i];
        
        // Handle null/undefined values properly
        if (
            (currentParam.businessFriendlyName || '') !== (originalParam.businessFriendlyName || '') ||
            (currentParam.units || '') !== (originalParam.units || '') ||
            (currentParam.selectedSensorType || '') !== (originalParam.selectedSensorType || '')
        ) {
            return true;
        }
    }

    return false;
};
    const handleNext = () => {
        //Navigate to Notification page
        navigate('/create-plant-details/notification');


    }

    return (
        <div className="create-plant-details" style={{ position: 'relative' }}>
            {loading && <Loader isOverlay={true} />}
            <ToastContainer
                className="toastify-message"
                position="top-right"
                autoClose={3000}
                aria-label="Asset Save Notification"
                style={{
                    marginTop: '150px',  // Increased from 60px to 120px
                    zIndex: 9999
                }}
            />
            <div className="create-plant-tabs">
                <div 
                className={`tab completed ${plantData || currentPlantData ? 'clickable' : ''}`}
                onClick={plantData || currentPlantData ? () => handleTabNavigation(1) : undefined}
                >
                <span className="tab-number">1</span>
                Enter Plant Details
                </div>
                <div className="tab active">
                    <span className="tab-number">2</span>
                    Build Asset Hierarchy
                </div>
                     <div 
                        className={`tab ${
                            plantData && plantData.assets && plantData.assets.length > 0 
                            ? 'clickable' 
                            : 'disabled'
                        }`}
                        onClick={
                            plantData && plantData.assets && plantData.assets.length > 0 
                            ? () => handleTabNavigation(3) 
                            : undefined
                        }
                        
                    >
                        <span className="tab-number">3</span>
                        Notification Setup
                    </div>

            </div>

            <div className="content-container">
                <div className="asset-hierarchy">
                    <h3>Asset Hierarchy</h3>
                     <button className="add-asset-btn" onClick={handleAddAsset}>Add New Asset</button>
                    <div className="search-box">
                        <img className='search-icon-plant' src={SearchIcon} alt='Search' />
                        <input
                            type="text"
                            placeholder="Search By Asset Name"
                            value={assetSearchText}
                            onChange={handleAssetSearchChange}
                        />
                    </div>
                   
                    <div className="asset-hierarchy assets-hierarchy">
                       {(Array.isArray(filteredAssets) ? filteredAssets : [])
                       .slice() // copy array so original isn’t mutated
                       .sort((a, b) => {
                        const aActive = a.status === 'Enabled' || a.status === 'Active';
                        const bActive = b.status === 'Enabled' || b.status === 'Active';
                        return aActive === bActive ? 0 : aActive ? -1 : 1; // active first
                            })
                        .map((asset, index) => {
                         const isSelected = selectedAssetFromHierarchy?.id === asset.id;
                         const isActive = asset.status === 'Enabled' || asset.status === 'Active';

                         return (
                                <div
                                 key={asset.id || index}
                                 className={`asset-item ${isSelected ? 'selected' : ''} ${isActive ? 'active' : 'inactive'}`}
                                 onClick={() => handleAssetClick(asset)}
                                   >
                                 <img className="asset-icon" src={assetIcon} alt="Asset Icon" />
                                 <span title={`${asset.type} - ${asset.name}`}>{`${asset.type} - ${asset.name}`}</span>
                                </div>
                            );
                        })}
                    </div>


                </div>                <div className="asset-details">
                    <div className="asset-details-header">
                        {selectedAssetFromHierarchy ? (
                            <h3>
                                {`${selectedAssetFromHierarchy.type} - ${selectedAssetFromHierarchy.name}`}
                            </h3>
                        ) : (
                            <h3>New Asset Configuration Details</h3>
                        )}
                        <div className="header-buttons">
                            <button 
                                className="download-config-btn-plant"
                                onClick={handleDownloadConfigFiles}
                                disabled={!selectedAssetFromHierarchy || loading}
                                title={!selectedAssetFromHierarchy ? 'Select an asset to download config files' : 'Download configuration files'}
                            >
                                <img src={downloadIcon} alt="Download" className="download-icon" />
                                Download Config Files
                            </button>
                            <button 
                                className={`save-btn-plant ${selectedAssetFromHierarchy && !hasAssetChanges() ? 'disabled' : ''}`}
                                onClick={handleSave}
                                disabled={!!(selectedAssetFromHierarchy && !hasAssetChanges())}
                                title={selectedAssetFromHierarchy && !hasAssetChanges() ? 'No changes to save' : 'Save asset'}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                    <div className='form-group'>
                    {/* First row - 3 columns */}
                    <div className="form-grid-three">
                        <div className="form-group-plant">
                        <label>Asset Type</label>
                        <select
                         value={selectedAsset}
                         onChange={handleTypeChange}
                         disabled={!!selectedAssetFromHierarchy} // ✅ Disable when editing existing asset
                         title={selectedAssetFromHierarchy ? "Cannot change asset type for existing assets" : "Select asset type"}
                         className={selectedAssetFromHierarchy ? 'disabled-select' : ''}
                        >
                         {Array.isArray(assetTypes) && assetTypes.length > 0 && assetTypes.map(type => (
                         <option key={type.typeName} value={type.typeName}>
                         {type.typeName}
                     </option>
                  ))}
               </select>
    {selectedAssetFromHierarchy && (
        <div className="field-disabled-note">
            Asset Type Cannot be changed for existing assets
        </div>
    )}
</div>
                        <div className="form-group-plant">
                            <label>Asset Status</label>
                            <select
                                value={assetStatus}
                                onChange={(e) => setAssetStatus(e.target.value)}
                            >
                                <option value="Enabled">Active</option>
                                <option value="Disabled">Inactive</option>
                            </select>
                        </div>
                        <div className="form-group-plant">
                            <label>Asset Name<span className="required">*</span></label>
                            <input
                                type="text"
                                value={assetName}
                                onChange={(e) => setAssetName(e.target.value)}
                                placeholder="Enter asset name"
                            />
                        </div>
                    </div>
                    {/* Second row - 3 columns */}
                    <div className="form-grid-three">
                        <div className="form-group-plant">
                            <label>Gateway / IoT Edge Device Type</label>
                            <select
                                value={gatewayDeviceType}
                                onChange={(e) => setGatewayDeviceType(e.target.value)}
                            >
                                <option value="">Select Device Type</option>
                                <option value="SLE">SLE</option>
                            </select>
                        </div>

                        <div className="form-group-plant">
                            <label>Gateway Serial Number<span className="required">*</span></label>
                            <input
                                type="text"
                                value={gatewayDeviceId}
                                onChange={handleGatewaySerialChange}
                                onBlur={handleGatewaySerialBlur}
                                className={gatewaySerialTouched && gatewaySerialError ? 'input-error' : undefined}
                            />
                            {gatewaySerialTouched && gatewaySerialError && (
                                <span className="form-error-text">{gatewaySerialError}</span>
                            )}
                        </div>

                        <div className="form-group-plant">
                         <label>SIM Number<span className="required">*</span></label>
                          <input
                              type="text"
                              value={simNumber}
                              onChange={handleSimNumberChange}
                              onBlur={handleSimNumberBlur}
                              className={simNumberTouched && simNumberError ? 'input-error' : ''}
                              placeholder="Enter SIM number (digits only)"
                               onKeyPress={(e) => {
                              // Prevent space key
                                if (e.key === ' ') {
                               e.preventDefault();
                                }
                              }}
                            />
                         {simNumberTouched && simNumberError && (
                         <span className="form-error-text">{simNumberError}</span>
                          )}
                        </div>
                    </div>
                    {/* Third row - 2 columns */}
                    <div className="form-grid-twos">
                        <div className="form-group-plant-controller">
                            <label>Controller Type</label>

                            <select
                                value={controllerType}
                                onChange={(e) => setControllerType(e.target.value)}
                            >
                                <option value="">Select Controller Type</option>
                                {availableControllerTypes.map((type) => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </select>

                        </div>
                        <div className="form-group-plant-number">
                            <label>Controller Serial Number <span className="required">*</span></label>
                            <input
                                type="text"
                                value={controllerDeviceId}
                                onChange={(e) => setControllerDeviceId(e.target.value)}

                            />
                        </div>
                    </div>
                    <div className="operational-parameters">
                        <h4>Operational Parameters / Attributes Settings</h4>
                       
                        <div className="parameters-table">
                            <table className="global-table">
                                <thead>
                                    <tr>
                                        <th className="col-large">Sensor</th>
                                        <th className="col-medium">Position</th>
                                        <th className="col-extra-large">Business Friendly Name</th>
                                        <th className="col-medium">Units</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(Array.isArray(parameters) ? parameters : []).map((param, index) => (
                                        <tr key={index}>
                                            <td className="text-wrap">{param.sensor}</td>
                                            <td className="text-wrap">{param.position || '-'}</td>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={param.businessFriendlyName}
                                                    placeholder="Enter here"
                                                    onChange={(e) => {
                                                        const updatedParameters = [...parameters];
                                                        updatedParameters[index].businessFriendlyName = e.target.value;
                                                        
                                                        // For custom probes, always keep unit options available
                                                        if (param.isUserConfigurable === true && validUoms.length > 0) {
                                                            const inputValue = e.target.value.trim();
                                                            
                                                            // Always provide all available UOMs for custom probes
                                                            const allUomsMap = new Map();
                                                            validUoms.forEach(sensorUom => {
                                                                sensorUom.uoms.forEach(uom => {
                                                                    allUomsMap.set(uom.code, uom);
                                                                });
                                                            });
                                                            updatedParameters[index].unitOptions = Array.from(allUomsMap.values());
                                                            
                                                            if (inputValue) {
                                                                // Check if the input matches a specific sensor type
                                                                const matchingSensor = validUoms.find(uom => 
                                                                    uom.sensorName.toLowerCase() === inputValue.toLowerCase()
                                                                );
                                                                
                                                                if (matchingSensor) {
                                                                    updatedParameters[index].selectedSensorType = matchingSensor.sensorName;
                                                                } else {
                                                                    updatedParameters[index].selectedSensorType = 'Custom';
                                                                }
                                                            } else {
                                                                // Keep unit options available even when business friendly name is empty
                                                                updatedParameters[index].selectedSensorType = '';
                                                            }
                                                        }
                                                        
                                                        setParameters(updatedParameters);
                                                    }}
                                                    style={{ width: '90%', maxWidth: 'none' }}
                                                />
                                            </td>
                                            <td>
                                                {param.isUserConfigurable === true ? (
                                                    // Custom probe - units dropdown always enabled
                                                    <select
                                                        value={param.units}
                                                        onChange={(e) => {
                                                            const updatedParameters = [...parameters];
                                                            updatedParameters[index].units = e.target.value;
                                                            setParameters(updatedParameters);
                                                        }}
                                                        style={{ 
                                                            width: '100%', 
                                                            color: 'white', 
                                                            height: '39px', 
                                                            borderRadius: '5px'
                                                        }}
                                                    >
                                                        <option value="">Select</option>
                                                        {param.unitOptions.map((unit, uIdx) => (
                                                            <option key={uIdx} value={unit.code}>
                                                                {unit.code}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : !param.hasUom ? (
                                                    // Sensors without UOM have no units
                                                    <span style={{ 
                                                        color: '#88929c', 
                                                        fontStyle: 'italic',
                                                        padding: '8px',
                                                        display: 'block'
                                                    }}>
                                                    </span>
                                                ) : (
                                                    // Standard probe - use original units
                                                    <select
                                                        value={param.units}
                                                        onChange={(e) => {
                                                            const updatedParameters = [...parameters];
                                                            updatedParameters[index].units = e.target.value;
                                                            setParameters(updatedParameters);
                                                        }}
                                                        style={{ width: '100%', color: 'white', height: '39px', borderRadius: '5px' }}
                                                    >
                                                        <option value="">Select</option>
                                                        {(Array.isArray(param.originalUnits) ? param.originalUnits : []).map((unit, unitIndex) => (
                                                            <option key={unitIndex} value={unit.code}>
                                                                {unit.code}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>

                            </table>
                            <button 
                                className={`save-btn ${selectedAssetFromHierarchy && !hasAssetChanges() ? 'disabled' : ''}`}
                                onClick={handleSave}
                                disabled={!!(selectedAssetFromHierarchy && !hasAssetChanges())}
                                title={selectedAssetFromHierarchy && !hasAssetChanges() ? 'No changes to save' : 'Save asset'}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                    </div>
                </div>
            </div>
         
             
            <div className="button-container">
                <button className="cancel-btn backs-button" onClick={handleBack}>
                    Previous
                </button>
                <button className="cancel-btn" onClick={handleCancel}>
                    Cancel
                </button>
                <button className="cancel-btn"
                    onClick={plantData && plantData.assets && plantData.assets.length > 0 ? handleNext : undefined}
                    disabled={!(plantData && plantData.assets && plantData.assets.length > 0)}
                                    >
                    Next
                </button>
            
            </div>
        </div>
    );
};

export default CreatePlantDetails;

